import * as React from 'react';
import { DataGrid } from '@SBGSports/referee-react';
import { useTranslations } from 'i18n';
import { useMemo } from 'react';
import { ColDef, RowSelectedEvent, SelectionChangedEvent } from 'ag-grid-community';
import { calcTableHeight } from '../../../reporting/helpers';
import _ from 'lodash';
import { CellInputRenderer } from './cell-input-renderer';
import { getRowStyle } from '../../focus-utils';
import { useFocusStoredOptions } from 'focus/use-focus-stored-options';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from '../../use-focus-import-selections';
import { DEFAULT_TABLE_COLUMN_DEF } from '../../../reporting/constants';

export interface FocusTableProps {
    tags: TagCount[];
    filterText: string;
    onValueChanged: (tagsToImport: Map<string, string>) => void;
}

export interface TagCount {
    tagName: string;
    tagCount: number;
}

export const TagImportTable: React.FC<FocusTableProps> = (props) => {
    const { tags, filterText, onValueChanged } = props;

    //Hooks
    const { __ } = useTranslations();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();
    const { addTagAliasEntry } = useFocusImportSelections();

    const columns: ColDef[] = [
        {
            flex: 2,
            headerName: __('smt_importer.focus_tag_type'),
            field: 'tags',
            sortable: true,
            sort: 'asc',
            comparator: (valueA: string, valueB: string, nodeA, nodeB) => {
                const isSelectedA = nodeA.isSelected();
                const isSelectedB = nodeB.isSelected();

                // order based on selection first
                if (isSelectedA && !isSelectedB) {
                    return -1;
                } else if (!isSelectedA && isSelectedB) {
                    return 1;
                }

                // else order alphabetically
                if (valueA < valueB) {
                    return -1;
                } else if (valueA > valueB) {
                    return 1;
                } else {
                    return 0;
                }
            },
            headerCheckboxSelection: true,
            checkboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
        },
        {
            flex: 2,
            headerName: __('smt_importer.openfield_annotation_name'),
            field: 'annotation',
            cellRenderer: CellInputRenderer,
        },
        {
            flex: 1,
            headerName: _.upperFirst(__('misc.count')),
            field: 'count',
        },
    ];

    const rowData = useMemo(() => {
        return tags.map((tag) => {
            return {
                tags: tag.tagName,
                annotation: '',
                count: tag.tagCount,
                placeholder: __('smt_importer.openfield_name_placeholder'),
                onChange: (value: string) => {
                    addTagAliasEntry(tag.tagName, value);
                    const update = focusStoredOptions.importedTagAlias.set(tag.tagName, value);
                    setFocusStoredOptions({ ...focusStoredOptions, importedTagAlias: update });
                    mixpanel.track(EVENTS.focus.tagImportAliasUpdated, { importedTagAlias: update });
                },
            };
        });
        // onChange should ideally be in the cell renderer or cell component, due to useFocusStoredOptions not working in cell -- requires further investigation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [__, setFocusStoredOptions, tags]);

    return (
        <div style={{ height: `${calcTableHeight(rowData)}` }}>
            <DataGrid
                testId="focus-table"
                defaultColDef={DEFAULT_TABLE_COLUMN_DEF}
                columnDefs={columns}
                rowData={rowData}
                getRowStyle={getRowStyle}
                quickFilterText={filterText}
                gridOptions={{
                    overlayNoRowsTemplate: `${__('misc.no_data_available')}`,
                    rowSelection: 'multiple',
                    suppressRowClickSelection: true,
                }}
                onGridReady={(params) => {
                    params.api.addEventListener('rowSelected', (event: RowSelectedEvent) => {
                        if (!event.node.isSelected()) {
                            event.node.setData({ ...event.data, annotation: '' });
                        } else if (event.node.isSelected() && !event.data.annotation) {
                            event.node.setData({ ...event.data, annotation: event.data.tags });
                        }
                    });

                    params.api.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
                        const tagsToImport = new Map<string, string>();
                        event.api.getSelectedNodes().forEach((n) => {
                            const savedAlias = focusStoredOptions.importedTagAlias?.get(n.data.tags);
                            const annotation = n.data.annotation ?? savedAlias;
                            const alias = annotation ?? n.data.tags;
                            tagsToImport.set(n.data.tags, alias);
                        });
                        setFocusStoredOptions({ ...focusStoredOptions, importedTagAlias: tagsToImport });
                        mixpanel.track(EVENTS.focus.tagImportAliasSelectionMade, { selectedTag: tagsToImport });
                        onValueChanged(tagsToImport);
                    });

                    params.api.getRenderedNodes().forEach((node) => {
                        // set node values if persisted alias exists
                        const alias = focusStoredOptions.importedTagAlias?.get(node.data.tags);
                        if (!!alias && node.data.annotation === '') {
                            node.setData({ ...node.data, annotation: alias });
                            node.setSelected(true);
                        }
                    });
                }}
                dense
            />
        </div>
    );
};
